/**
 * PlanCreateModal
 * Modal for creating a new plan with name and asset selection
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Loader2, Sparkles, Globe } from 'lucide-react';
import { plansApi } from './api/plansApi';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PlanCreateModal({ open, onOpenChange, onSuccess }) {
  const [name, setName] = useState('');
  const [assetId, setAssetId] = useState('');
  const [assets, setAssets] = useState([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (open) {
      loadAssets();
    }
  }, [open]);

  const loadAssets = async () => {
    setLoadingAssets(true);
    try {
      const response = await axios.get(`${API}/api/assets`);
      // Filter to only show ready assets
      const readyAssets = (response.data || []).filter(a => a.status === 'ready');
      setAssets(readyAssets);
    } catch (error) {
      console.error('Failed to load assets:', error);
      toast.error('Failed to load assets');
    } finally {
      setLoadingAssets(false);
    }
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error('Please enter a plan name');
      return;
    }
    if (!assetId) {
      toast.error('Please select an asset');
      return;
    }

    setCreating(true);
    try {
      const plan = await plansApi.create(name.trim(), assetId);
      toast.success('Plan created successfully');
      onOpenChange(false);
      setName('');
      setAssetId('');
      if (onSuccess) {
        onSuccess(plan);
      }
    } catch (error) {
      console.error('Failed to create plan:', error);
      toast.error(error.response?.data?.detail || 'Failed to create plan');
    } finally {
      setCreating(false);
    }
  };

  const handleClose = () => {
    if (!creating) {
      onOpenChange(false);
      setName('');
      setAssetId('');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]" data-testid="plan-create-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-500" />
            Create New Plan
          </DialogTitle>
          <DialogDescription>
            Create a marketing campaign plan linked to an existing asset.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Plan Name */}
          <div className="space-y-2">
            <Label htmlFor="plan-name">Plan Name *</Label>
            <Input
              id="plan-name"
              placeholder="e.g., Q1 Growth Campaign"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={creating}
              data-testid="plan-name-input"
            />
          </div>

          {/* Asset Selection */}
          <div className="space-y-2">
            <Label htmlFor="asset-select">Select Asset *</Label>
            {loadingAssets ? (
              <div className="flex items-center gap-2 text-muted-foreground py-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading assets...
              </div>
            ) : assets.length === 0 ? (
              <div className="text-sm text-muted-foreground py-2 px-3 bg-muted/50 rounded-lg">
                No assets available. Please import an asset first.
              </div>
            ) : (
              <Select value={assetId} onValueChange={setAssetId} disabled={creating}>
                <SelectTrigger id="asset-select" data-testid="asset-select">
                  <SelectValue placeholder="Choose an asset..." />
                </SelectTrigger>
                <SelectContent>
                  {assets.map((asset) => (
                    <SelectItem key={asset.id} value={asset.id}>
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-muted-foreground" />
                        <span>{asset.name}</span>
                        <span className="text-xs text-muted-foreground truncate max-w-[200px]">
                          ({asset.url})
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={creating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={creating || !name.trim() || !assetId}
            className="bg-purple-500 hover:bg-purple-600 text-white"
            data-testid="create-plan-btn"
          >
            {creating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Create Plan
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
